import Link from "next/link";
import { timeAgo, PubPost } from "../_lib/api";
import { LockIcon, PinIcon } from "./icons";
import { FeedVote } from "./PublicFeedVote";
import { VerifiedBadge } from "./VerifiedBadge";
import { StaffBadge } from "./StaffBadge";

// One thread on the floor, set like a ledger line: the number first, then the
// category as a label, the title in the serif, the words underneath.
// Whole row is the link (stretched anchor); author and category stay clickable.
export function PostRow({
  post,
  showCategory = true,
}: {
  post: PubPost;
  showCategory?: boolean;
  index?: number;
}) {
  return (
    <li className="group relative grid grid-cols-[3.25rem_minmax(0,1fr)] gap-x-3 px-3 py-3.5 transition-colors hover:bg-blue-50/50 sm:grid-cols-[3.75rem_minmax(0,1fr)_auto] sm:px-4">
      {/* the number, with the arrows: vote from the floor itself */}
      <div className="pt-0.5">
        <FeedVote postId={post.id} score={post.score} commentCount={post.commentCount} />
      </div>

      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-x-1.5 text-[11px] leading-4 text-slate-500">
          {post.isPinned && (
            <span className="inline-flex items-center gap-1 font-semibold text-slate-700">
              <PinIcon /> Pinned
            </span>
          )}
          {post.isLocked && (
            <span className="inline-flex items-center gap-1 text-slate-500">
              <LockIcon /> Locked
            </span>
          )}
          {post.isFresh && (
            <span className="fp-mono font-semibold uppercase tracking-[0.12em] text-blue-700">New on the floor</span>
          )}
          {(post.isPinned || post.isLocked || post.isFresh) && <span className="text-slate-300">·</span>}
          {showCategory && (
            <>
              <Link
                href={`/c/${post.category.slug}`}
                className="fp-inline relative z-10 font-semibold uppercase tracking-[0.08em] text-blue-700 hover:underline"
              >
                {post.category.name}
              </Link>
              <span className="text-slate-300">·</span>
            </>
          )}
          {post.author.handle ? (
            <Link href={`/u/${post.author.handle}`} className="fp-inline relative z-10 font-medium text-slate-600 hover:text-blue-700 hover:underline">
              {post.author.name}
            </Link>
          ) : (
            <span className="font-medium text-slate-600">{post.author.name}</span>
          )}
          {post.author.lpSlug && <VerifiedBadge slug={post.author.lpSlug} />}
          {post.author.isStaff && <StaffBadge />}
          <span className="text-slate-300">·</span>
          <time dateTime={post.createdAt} className="fp-mono tabular-nums">{timeAgo(post.createdAt)}</time>
        </p>
        <h2 className="fp-serif mt-1 text-[17px] font-medium leading-[1.3] text-slate-900 group-hover:text-blue-800 sm:text-[19px]">
          <Link href={`/t/${post.slug}`}>
            <span className="absolute inset-0" aria-hidden />
            {post.title}
          </Link>
        </h2>
        {post.bodyPreview && (
          <p className="mt-1 line-clamp-2 text-[13px] leading-5 text-slate-500">{post.bodyPreview}</p>
        )}
      </div>

      {post.imageUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={post.imageUrl}
          alt=""
          loading="lazy"
          width={64}
          height={64}
          className="hidden h-16 w-16 shrink-0 rounded-md border border-slate-200 object-cover sm:block"
        />
      )}
    </li>
  );
}
